import { guardGameSurface } from './186-game-surface.mjs';
import { createDragSurface as createGameDragSurface } from './186-drag-surface.mjs';
import {
  blockPlacement,
  blockClearLines,
} from '../../../packages/domain/src/56-score-board-games.mjs';
import { paintGameBoard } from './35-game-board.mjs';

/** Exact landing ghosts and bounded, cancellable clear scenes for Patchwork Dock.
 * Advisory snapshots never supply points or accepted actions. Native touch scroll,
 * keyed buttons and focus remain the input owners; no pointer capture is used. */
export function createBlockPlayback(ctx, board, { settle, onClear } = {}) {
  let generation = 0,
    pending = false,
    active = null;
  const animations = new Set(),
    effects = new Set();
  const listeners = [];
  function clearPreview() {
    for (const cell of board.children) {
      delete cell.dataset.blockGhost;
      delete cell.dataset.blockLine;
    }
  }
  function preview(event) {
    clearPreview();
    const state = board.gameState;
    if (pending || state?.gameId !== 'block-fit' || state.completed) return;
    const target = event.target?.closest?.('[data-cell]');
    if (!target || !board.contains(target) || target.disabled) return;
    showBlockFootprint(board, state.data, Number(target.dataset.cell));
  }
  for (const name of ['pointerover', 'pointerdown', 'focusin']) {
    board.addEventListener(name, preview);
    listeners.push([name, preview]);
  }
  for (const name of ['pointerleave', 'pointercancel', 'focusout']) {
    board.addEventListener(name, clearPreview);
    listeners.push([name, clearPreview]);
  }
  function cancel() {
    generation++;
    pending = false;
    active = null;
    for (const animation of animations) animation.cancel();
    for (const effect of effects) effect.remove();
    animations.clear();
    effects.clear();
    clearPreview();
  }
  const eventDocument = document;
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  function suspended() {
    if (!active || (!document.hidden && !media.matches)) return;
    const accepted = active;
    cancel();
    if (board.isConnected)
      paintGameBoard(ctx, board, accepted, () => {}, { disabled: true, motion: false });
    settle?.(accepted);
  }
  eventDocument.addEventListener?.('visibilitychange', suspended);
  media.addEventListener?.('change', suspended);
  function animate(node, frames, duration) {
    if (typeof node?.animate !== 'function') return;
    const animation = node.animate(frames, { duration, easing: 'ease-out', fill: 'forwards' });
    animations.add(animation);
  }
  function effect(className, text = '') {
    const node = ctx.e('span', `game-block-fx ${className}`, text);
    node.setAttribute('aria-hidden', 'true');
    effects.add(node);
    board.append(node);
    return node;
  }
  function cellAt(index) {
    return [...board.children].find((cell) => cell.dataset.cell === String(index));
  }
  function bounds(index) {
    const rect = cellAt(index)?.getBoundingClientRect(),
      origin = board.getBoundingClientRect();
    return rect
      ? { x: rect.left - origin.left, y: rect.top - origin.top, w: rect.width, h: rect.height }
      : null;
  }
  function beam(axis, lane) {
    const first = bounds(axis === 'row' ? lane * 6 : lane);
    const last = bounds(axis === 'row' ? lane * 6 + 5 : lane + 30);
    if (!first || !last) return;
    const node = effect('game-block-beam');
    node.dataset.axis = axis;
    Object.assign(node.style, {
      left: `${first.x}px`,
      top: `${first.y}px`,
      width: `${last.x + last.w - first.x}px`,
      height: `${last.y + last.h - first.y}px`,
    });
    animate(
      node,
      [
        { opacity: 0, transform: 'scale(.9)' },
        { opacity: 1, offset: 0.22, transform: 'scale(1)' },
        { opacity: 0.8, offset: 0.55, transform: 'scale(1.04)' },
        { opacity: 0, transform: 'scale(1.08)' },
      ],
      360,
    );
  }
  function shards(clear) {
    for (const index of clear.cells.slice(0, 36)) {
      const rect = bounds(index);
      if (!rect) continue;
      for (let shard = 0; shard < 3; shard++) {
        const node = effect('game-block-shard');
        node.dataset.sourceCell = String(index);
        node.style.left = `${rect.x + rect.w / 2}px`;
        node.style.top = `${rect.y + rect.h / 2}px`;
        node.style.width = `${Number(Math.max(5, rect.w * 0.23).toFixed(2))}px`;
        node.style.height = `${Number(Math.max(5, rect.h * 0.23).toFixed(2))}px`;
        node.style.setProperty(
          '--piece-colour',
          cellAt(index).style.getPropertyValue('--piece-colour'),
        );
        const angle = (index % 6) * 0.4 + (shard * Math.PI * 2) / 3;
        const x = Math.cos(angle) * rect.w * 1.6;
        const y = Math.sin(angle) * rect.h * 1.6 - rect.h * 0.8;
        animate(
          node,
          [
            { opacity: 1, transform: 'translate(0,0) rotate(0) scale(.6)' },
            {
              opacity: 1,
              transform: `translate(${x * 0.55}px,${y * 0.7}px) rotate(45deg) scale(1)`,
              offset: 0.4,
            },
            {
              opacity: 0,
              transform: `translate(${x}px,${y}px) rotate(${90 + shard * 35}deg) scale(.2)`,
            },
          ],
          400,
        );
      }
    }
  }
  async function wait() {
    const batch = [...animations],
      nodes = [...effects];
    await Promise.all(batch.map((animation) => animation.finished.catch(() => {})));
    for (const animation of batch) {
      animation.cancel();
      animations.delete(animation);
    }
    for (const node of nodes) {
      node.remove();
      effects.delete(node);
    }
  }
  function play(previous, finalState, events) {
    cancel();
    const clear = events.find((event) => event.type === 'clear');
    if (
      finalState.gameId !== 'block-fit' ||
      !clear ||
      !board.isConnected ||
      document.hidden ||
      media.matches ||
      typeof board.children[0]?.animate !== 'function'
    )
      return null;
    const token = generation;
    pending = true;
    active = finalState;
    const current = () =>
      token === generation && board.isConnected && !document.hidden && !media.matches;
    return (async () => {
      try {
        const snapshot = structuredClone(previous);
        snapshot.data.board = [...clear.boardBefore];
        paintGameBoard(ctx, board, snapshot, () => {}, { disabled: true, motion: false });
        for (const row of clear.rows || []) beam('row', row);
        for (const col of clear.columns || []) beam('column', col);
        shards(clear);
        const geometry = clear.cells.map(bounds).filter(Boolean);
        const centre = {
          x: geometry.reduce((sum, rect) => sum + rect.x + rect.w / 2, 0) / geometry.length,
          y: geometry.reduce((sum, rect) => sum + rect.y + rect.h / 2, 0) / geometry.length,
        };
        if (clear.rows?.length && clear.columns?.length) {
          const crossing = bounds(clear.rows[0] * 6 + clear.columns[0]);
          centre.x = crossing.x + crossing.w / 2;
          centre.y = crossing.y + crossing.h / 2;
        }
        for (const index of clear.cells)
          animate(
            cellAt(index)?.firstChild,
            [
              { opacity: 1, transform: 'scale(1)' },
              { opacity: 0, transform: 'scale(.3)' },
            ],
            240,
          );
        const allClear = events.find((event) => event.type === 'all-clear');
        const title = allClear
          ? 'DECK CLEAR!'
          : clear.rows?.length && clear.columns?.length
            ? 'CROSS CLEAR!'
            : clear.lines > 1
              ? `${clear.lines} LINE BLAST!`
              : clear.combo > 1
                ? `${clear.combo} CLEAR STREAK!`
                : 'LINE CLEAR!';
        const award = effect('game-block-award', title);
        const origin = board.getBoundingClientRect();
        const width = Math.min(250, origin.width * 0.9);
        Object.assign(award.style, {
          width: `${width}px`,
          left: `${Math.max(0, Math.min(origin.width - width, centre.x - width / 2))}px`,
          top: `${Math.max(8, Math.min(origin.height - 64, centre.y - 28))}px`,
        });
        award.dataset.earnedPoints = String(clear.points + (allClear?.points || 0));
        award.append(
          ctx.e(
            'small',
            '',
            `+${clear.points + (allClear?.points || 0)} earned${clear.combo > 1 ? ` · Streak ${clear.combo}` : ''}`,
          ),
        );
        animate(
          award,
          [
            { opacity: 0, transform: 'translateY(12px) scale(.8)' },
            { opacity: 1, transform: 'translateY(0) scale(1)', offset: 0.2 },
            { opacity: 1, offset: 0.7 },
            { opacity: 0, transform: 'translateY(-12px) scale(1.02)' },
          ],
          530,
        );
        await wait();
        if (!current()) return;
        paintGameBoard(ctx, board, finalState, () => {}, { disabled: true, motion: false });
        onClear?.(clear);
      } finally {
        if (token === generation) {
          pending = false;
          active = null;
          paintGameBoard(ctx, board, finalState, () => {}, { disabled: true, motion: false });
          settle?.(finalState);
        }
      }
    })();
  }
  return {
    play,
    cancel,
    dispose() {
      cancel();
      for (const [name, listener] of listeners) board.removeEventListener(name, listener);
      eventDocument.removeEventListener?.('visibilitychange', suspended);
      media.removeEventListener?.('change', suspended);
    },
  };
}

function clearBlockFootprint(board) {
  for (const cell of board.children) {
    delete cell.dataset.blockGhost;
    delete cell.dataset.blockLine;
  }
}
function showBlockFootprint(board, data, anchor, piece = data.piece, colour = data.selected) {
  clearBlockFootprint(board);
  const footprint = blockPlacement(data, anchor, piece);
  if (!footprint.length) return false;
  const projected = data.board.map((value, i) => value || footprint.includes(i));
  const lines = new Set(blockClearLines(projected).cells);
  for (const cell of board.children) {
    const index = Number(cell.dataset.cell);
    if (footprint.includes(index)) {
      cell.dataset.blockGhost = 'true';
      cell.style?.setProperty('--block-ghost-colour', `var(--game-colour-${colour})`);
    }
    if (lines.has(index)) cell.dataset.blockLine = 'true';
  }
  return true;
}

/** A deliberate tray drag carries an original piece above the finger. Tap and
 * keyboard selection still use existing options. Capture is released before
 * accepted selection/placement; stale rounds or cancellation cannot drop pieces. */
export function attachBlockDrag(ctx, board, tray, readState, move) {
  let drag = null,
    suppressClick = false;
  const returns = new Set();
  const surface = createGameDragSurface(board);
  const eventDocument = document;
  const media = globalThis.window?.matchMedia?.('(prefers-reduced-motion: reduce)');
  const suspended = () => {
    if (eventDocument.hidden || media?.matches) cancel();
  };
  eventDocument.addEventListener?.('visibilitychange', suspended);
  media?.addEventListener?.('change', suspended);
  function cancel() {
    surface.reset();
    const current = drag;
    drag = null;
    if (current) {
      if (current.button.hasPointerCapture?.(current.id))
        current.button.releasePointerCapture(current.id);
      current.ghost?.remove();
    }
    for (const returned of returns) {
      returned.animation.cancel();
      returned.node.remove();
    }
    returns.clear();
    clearBlockFootprint(board);
  }
  function returnRejected(current) {
    const ghost = current.ghost;
    const origin = current.button.querySelector('.game-piece-preview')?.getBoundingClientRect?.();
    const start = ghost?.getBoundingClientRect?.();
    if (!origin || !start || typeof ghost.animate !== 'function' || media?.matches) return cancel();
    current.ghost = null;
    cancel();
    const scale = origin.width / start.width;
    const boardOrigin = board.getBoundingClientRect();
    ghost.style.transition = 'none';
    const animation = ghost.animate(
      [
        {
          transform: `translate3d(${start.left - boardOrigin.left}px, ${start.top - boardOrigin.top}px, 0)`,
          opacity: 0.9,
        },
        {
          transform: `translate3d(${origin.left - boardOrigin.left}px, ${origin.top - boardOrigin.top}px, 0) scale(${scale})`,
          opacity: 0.3,
        },
      ],
      { duration: 180, easing: 'cubic-bezier(.2,.8,.3,1)', fill: 'forwards' },
    );
    const returned = { node: ghost, animation };
    returns.add(returned);
    void animation.finished
      .catch(() => {})
      .then(() => {
        ghost.remove();
        returns.delete(returned);
      });
  }
  function down(event) {
    if (drag || event.button !== 0 || event.isPrimary === false) return;
    const button = event.target?.closest?.('[data-option]');
    const state = readState(),
      slot = Number(button?.dataset.option) - 1;
    if (
      state.gameId !== 'block-fit' ||
      state.completed ||
      !button ||
      button.disabled ||
      !state.data.tray[slot]
    )
      return;
    cancel();
    suppressClick = false;
    const rect = button.querySelector('.game-piece-preview')?.getBoundingClientRect?.();
    const fraction = (value, start, size) =>
      size > 0 ? Math.max(0, Math.min(1, (value - start) / size)) : 0.5;
    drag = {
      pointerType: event.pointerType || 'mouse',
      grabX: fraction(event.clientX, rect?.left, rect?.width),
      grabY: fraction(event.clientY, rect?.top, rect?.height),
      id: event.pointerId,
      button,
      slot,
      state,
      piece: state.data.tray[slot],
      x: event.clientX,
      y: event.clientY,
      anchor: -1,
      active: false,
      ghost: null,
    };
    // Capture before the first move can leave the tray. The distance threshold
    // still separates native taps from intentional drags, but cannot own routing.
    button.setPointerCapture?.(event.pointerId);
  }
  function update(event) {
    if (!drag || event.pointerId !== drag.id) return;
    if (readState() !== drag.state || document.hidden) return cancel();
    if (!drag.active && Math.hypot(event.clientX - drag.x, event.clientY - drag.y) < 7) return;
    if (!drag.active) {
      drag.active = true;
      drag.ghost = drag.button.querySelector('.game-piece-preview').cloneNode(true);
      drag.ghost.classList.add('game-block-drag');
      drag.ghost.setAttribute('aria-hidden', 'true');
      drag.ghost.style.setProperty('--piece-colour', `var(--game-colour-${drag.slot})`);
      board.append(drag.ghost);
    }
    event.preventDefault();
    drag.geometry ??= {
      first: board.querySelector('[data-cell="0"]').getBoundingClientRect(),
      second: board.querySelector('[data-cell="1"]').getBoundingClientRect(),
      origin: board.getBoundingClientRect(),
    };
    const { first, second, origin } = drag.geometry;
    const step = second.left - first.left;
    const columns = Math.max(...drag.piece.map(([x]) => x)) + 1;
    const rows = Math.max(...drag.piece.map(([, y]) => y)) + 1;
    const gap = Math.max(0, step - first.width);
    const width = columns * step - gap;
    const height = rows * step - gap;
    // A mouse/stylus keeps the grabbed point under its precise cursor. Touch
    // retains a lifted stencil so the finger cannot hide its landing footprint.
    const touch = drag.pointerType === 'touch';
    const left = event.clientX - width * (touch ? 0.5 : drag.grabX),
      top = event.clientY - (touch ? height + 20 : height * drag.grabY);
    const x = Math.round((left - first.left) / step),
      y = Math.round((top - first.top) / step);
    drag.anchor = x >= 0 && x < 6 && y >= 0 && y < 6 ? y * 6 + x : -1;
    const legal = showBlockFootprint(board, drag.state.data, drag.anchor, drag.piece, drag.slot);
    // A legal ghost is the exact stencil, not a half-cell-offset approximation.
    // Pointer motion stays on one composited surface; board/tray never reflow.
    Object.assign(drag.ghost.style, {
      left: '0',
      top: '0',
      width: `${width}px`,
      height: `${height}px`,
      gridTemplateColumns: `repeat(${columns},1fr)`,
      gridTemplateRows: `repeat(${rows},1fr)`,
    });
    surface.carry(
      drag.ghost,
      `translate3d(${(legal ? first.left + x * step : left) - origin.left}px, ${(legal ? first.top + y * step : top) - origin.top}px, 0)`,
    );
    drag.ghost.dataset.legal = String(legal);
  }
  function up(event) {
    if (!drag || event.pointerId !== drag.id) return;
    update(event);
    if (!drag) return;
    surface.flush();
    const current = drag;
    const valid =
      current.active &&
      readState() === current.state &&
      !document.hidden &&
      blockPlacement(current.state.data, current.anchor, current.piece).length;
    if (current.active) {
      event.preventDefault();
      suppressClick = true;
    }
    if (!valid && current.active && readState() === current.state && !document.hidden) {
      returnRejected(current);
      return;
    }
    cancel();
    if (!valid) return;
    if (readState().data.selected !== current.slot)
      move({ type: 'option', index: current.slot + 1 });
    // Permission or paused state may reject selection; inspect the actual state.
    if (
      readState().data.selected === current.slot &&
      blockPlacement(readState().data, current.anchor).length
    )
      move({ type: 'cell', index: current.anchor });
  }
  function click(event) {
    if (!suppressClick) return;
    suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  }
  const unguard = guardGameSurface(tray, { cancel });
  const listeners = [
    ['pointerdown', down],
    ['pointermove', update],
    ['pointerup', up],
    [
      'pointercancel',
      (event) => {
        if (drag?.id === event.pointerId) cancel();
      },
    ],
    [
      'lostpointercapture',
      (event) => {
        if (drag?.id === event.pointerId) cancel();
      },
    ],
    ['click', click],
  ];
  for (const [name, handler] of listeners)
    tray.addEventListener(name, handler, name === 'click' ? true : false);
  return {
    cancel,
    dispose() {
      cancel();
      unguard();
      for (const [name, handler] of listeners)
        tray.removeEventListener(name, handler, name === 'click' ? true : false);
      eventDocument.removeEventListener?.('visibilitychange', suspended);
      media?.removeEventListener?.('change', suspended);
    },
  };
}
